import { CreateMatchPlayerCommandOfMatchPlayer, EventTypeEnum, EventVm, MatchDetailVm, PlayerPositionEnum } from '../utils/api';
import { compareEventPosition } from './eventPosition';

const chronological = (a: EventVm, b: EventVm): number =>
    a.periodId - b.periodId || (b.minute * 60 + b.seconds) - (a.minute * 60 + a.seconds)
    || compareEventPosition(
        { shootoutOrder: a.shootoutOrder, id: a.id > 0 ? a.id : Number.MAX_SAFE_INTEGER },
        { shootoutOrder: b.shootoutOrder, id: b.id > 0 ? b.id : Number.MAX_SAFE_INTEGER });

/** Same inclusion rule as ApplyGoalkeeperTimeline, including a change's own row. */
const inForce = (change: EventVm, target: EventVm): boolean => {
    if (change.periodId !== target.periodId) return change.periodId < target.periodId;
    const clockDifference = change.minute * 60 + change.seconds - target.minute * 60 - target.seconds;
    if (clockDifference !== 0) return clockDifference > 0;
    if ((change.shootoutOrder != null) !== (target.shootoutOrder != null)) return change.shootoutOrder == null;
    if (change.shootoutOrder != null && change.shootoutOrder !== target.shootoutOrder)
        return change.shootoutOrder < target.shootoutOrder!;
    if (change.id === target.id && target.shootoutOrder != null) return true;
    return change.id > 0 && (target.id <= 0 || change.id <= target.id);
};

/** A numbered row has a position even when its clock equals every other PSO row. */
export const goalkeepersAtShootoutRow = (
    match: MatchDetailVm, events: EventVm[], target: EventVm
): { homeGkId?: number; awayGkId?: number } => {
    const players = (match.players as CreateMatchPlayerCommandOfMatchPlayer[]) || [];
    const keeperFor = (teamId: number, stamp?: number): number | undefined => {
        const onTeam = (id?: number): boolean => id != null && players.some(p => p.id === id && p.teamId === teamId);
        const changes = events.filter(e => e.typeId === EventTypeEnum.Goalkeeper_Change && e.goalkeeperChange?.teamId === teamId
            && onTeam(e.goalkeeperChange.newGoalkeeperMatchPlayerId)).sort(chronological);
        const prior = changes.filter(e => inForce(e, target));
        const lineupKeepers = players.filter(p => p.teamId === teamId && p.positionId === PlayerPositionEnum.Goalkeeper);
        // The server carries the pre-reorder start onto the new first change's Previous.
        // Read that canonical boundary, not the lowest-id change (whose Previous is now a chain link).
        const derived = prior.length ? prior[prior.length - 1].goalkeeperChange?.newGoalkeeperMatchPlayerId
            : changes[0]?.goalkeeperChange?.previousGoalkeeperMatchPlayerId
                ?? (lineupKeepers.length === 1 ? lineupKeepers[0].id : undefined);
        // A hub burst can briefly leave old stamps alongside a new order. Don't trust a contradiction.
        if (onTeam(stamp) && (changes.length === 0 || derived == null || stamp === derived)) return stamp;
        return onTeam(derived) ? derived : undefined;
    };
    return {
        homeGkId: keeperFor(match.homeTeamId, target.activeHomeGoalkeeperMatchPlayerId),
        awayGkId: keeperFor(match.awayTeamId, target.activeAwayGoalkeeperMatchPlayerId),
    };
};
